import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Button } from '@common/components/button';
import Alert from '@common/components/alert';
import Icon from '@common/components/icon';
import { TextInput } from '@common/components/form/inputs/text/text-input';
import { normaliseDomain } from './domain';
import type { ResearchErrorKind } from '../../hooks/use-trial-research';

type Props = {
  initialValue: string;
  error: ResearchErrorKind | null;
  isSubmitting: boolean;
  onSubmit: (domain: string) => void;
};

const ERROR_KEYS: Record<ResearchErrorKind, string> = {
  not_allowed: 'authentication:trial_domain_not_allowed',
  rate_limited: 'authentication:trial_domain_rate_limited',
  invalid: 'authentication:trial_domain_invalid',
  failed: 'authentication:trial_research_failed',
};

export const DomainStep = ({
  initialValue, error, isSubmitting, onSubmit,
}: Props) => {
  const { t } = useTranslation();
  const [value, setValue] = React.useState(initialValue);
  const [touched, setTouched] = React.useState(false);

  const domain = normaliseDomain(value);
  const showInvalid = touched && value.trim().length > 0 && !domain;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setTouched(true);

    if (domain) onSubmit(domain);
  };

  return (
    <>
      <h2><Trans i18nKey="authentication:start_free_trial" /></h2>
      <div className="Authentication__Form__Description"><Trans i18nKey="authentication:trial_domain_description" /></div>

      <form className="Form" onSubmit={handleSubmit}>
        {error && (
          <Alert type="warning" title={t(ERROR_KEYS[error])}>
            {error === 'failed' && (
              <Trans
                i18nKey="authentication:error_creating_trial_description"
                components={[<a onClick={() => window.Intercom && window.Intercom('show')} role="link" />]}
              />
            )}
          </Alert>
        )}
        <div className="Form__Row">
          <div className="Form__Group">
            <label className="Form__Label" htmlFor="trial-domain">{t('authentication:trial_domain_label')}</label>
            <div className="FreeTrial__DomainInput">
              <Icon type="public" size="small" />
              <TextInput
                id="trial-domain"
                name="company-website"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                onBlur={() => setTouched(true)}
                placeholder="company.com"
                type="text"
                // Not a login field: keep 1Password, LastPass and browser autofill out of it.
                autoComplete="off"
                data-1p-ignore
                data-lpignore="true"
                data-form-type="other"
                autoFocus
                error={showInvalid}
              />
            </div>
            {showInvalid && <div className="FreeTrial__Hint">{t('authentication:trial_domain_invalid')}</div>}
          </div>
        </div>
        <Button
          type="primary"
          size="fill"
          buttonType="submit"
          disabled={!domain}
          isLoading={isSubmitting}
          iconRight="arrow_forward"
        >
          <Trans i18nKey="authentication:trial_domain_submit" />
        </Button>
      </form>
      <div className="Authentication__Form__Consent">
        <Trans i18nKey="authentication:trial_domain_no_account" />
        {' '}
        <Trans i18nKey="authentication:already_have_account" components={[<a href="/auth/login" />]} />
      </div>
    </>
  );
};
